import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import StandaloneSearchInput from "../../common/StandaloneSearchInput";
import SimpleToolbar from "../../common/SimpleToolbar";
import useDataGridSearchDebounced from "../../../hooks/useDataGridSearchDebounced";

const CreditCaptureStatus = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Credit Capture Status"]?.edit;
  const {
    data: creditCaptureStatuses,
    loading,
    error,
  } = useFirestore("CreditCaptureStatus");
  const [selectedCreditCaptureStatus, setSelectedCreditCaptureStatus] =
    useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([
    { field: "Credit_Capture_Status", sort: "asc" },
  ]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "TP Credit Capture Status";

  // Search hook with debouncing
  const { filteredData, searchValue, setSearchValue } =
    useDataGridSearchDebounced(creditCaptureStatuses || [], [
      "Credit_Capture_Status",
      "Active",
    ]);

  const getActiveDisplay = (active) => {
    if (active === -1) {
      return (
        <Chip
          label="Active"
          color="success"
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      );
    } else if (active === 0) {
      return (
        <Chip
          label="Inactive"
          color="error"
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      );
    } else {
      return (
        <Chip
          label="Unknown"
          color="default"
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      );
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "Credit_Capture_Status",
        headerName: "Credit Capture Status",
        flex: 2,
        minWidth: 300,
      },
      {
        field: "Active",
        headerName: "Active",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => getActiveDisplay(params.value),
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((creditCaptureStatus, index) => ({
      id: creditCaptureStatus.id || index,
      ...creditCaptureStatus,
    }));
  }, [filteredData]);

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedCreditCaptureStatus(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCreditCaptureStatus(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  const exportColumnOrder = columns.map((col) => col.field);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>Error fetching credit capture statuses: {error.message}</p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        <StandaloneSearchInput
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Search credit capture statuses..."
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="CreditCaptureStatus"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={exportColumnOrder}
              exportData={creditCaptureStatuses}
              templateFields={exportColumnOrder}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="CreditCaptureStatus" />
          )}
        </Box>
        <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            slots={{
              toolbar: SimpleToolbar,
            }}
            slotProps={{
              toolbar: {
                fileName: "credit-capture-status",
              },
            }}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedCreditCaptureStatus && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          creditCaptureStatusDetail={selectedCreditCaptureStatus}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreditCaptureStatus;
